// RawMaterialControl endpoints


export interface RawMaterialControlDTO {
	uuid: string;
	jsonData: string;
	createdAt?: string;
	orderId?: string;
	isDraft?: boolean;
	isArchived?: boolean;
	isCancelled?: boolean;
	lastUpdated?: string;
	author?: string;
}


export interface RawMaterialControlDTOMinimal {
	uuid: string;
	orderId?: string;
}

export interface RawMaterialControlGrouped {
	 drafts: RawMaterialControlDTOMinimal[];
  nonDrafts: RawMaterialControlDTOMinimal[];
  cancelled: RawMaterialControlDTOMinimal[];
}


const BASE_URL = `${process.env.REACT_APP_API_URL}/oi-raw-material-control`;

function getFilenameFromContentDisposition(contentDisposition: string | null, fallback: string): string {
	if (!contentDisposition) {
		return fallback;
	}

	const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8Match?.[1]) {
		return decodeURIComponent(utf8Match[1]);
	}

	const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
	if (asciiMatch?.[1]) {
		return asciiMatch[1];
	}

	return fallback;
}

// Create RawMaterialControl (returns uuid)
// Only send JsonData and IsDraft to match backend create payload
export async function CreateRawMaterialControl(payload: string, isDraft: boolean, author: string): Promise<string | null> {
	try {
		const payloadObj = {
			JsonData: payload,
			IsDraft: isDraft,
			Author: author,
		};
		const response = await fetch(`${BASE_URL}/`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payloadObj),
		});
		if (response.ok) {
			console.log("CreateRawMaterialControl response:", response);
			return await response.text(); // Assuming the API returns the uuid as plain text. Adjust if it's JSON.
		}
		return null;
	} catch (error) {
		console.error('Failed to create RawMaterialControl', error);
		return null;
	}
}

// Update RawMaterialControl (PATCH /{uuid})
export async function UpdateRawMaterialControl(uuid: string, payload: string, isDraft: boolean): Promise<boolean> {
	try {
        const payloadObj = {
            JsonData: payload,
            IsDraft: isDraft,
        }
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payloadObj),
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to update RawMaterialControl', error);
		return false;
	}
}

// Get paginated RawMaterialControls
export async function getRawMaterialControlsPaginate(page: number, pageSize = 10): Promise<RawMaterialControlDTO[] | null> {
	try {
		const url = `${BASE_URL}/page/${page}?pageSize=${pageSize}`;
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			return await response.json();
		}
		return null;
	} catch (error) {
		console.error('Failed to fetch paginated RawMaterialControls', error);
		return null;
	}
}

// Get RawMaterialControl by uuid
export async function GetRawMaterialControl(uuid: string): Promise<RawMaterialControlDTO | null> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			return await response.json();
		}
		if (response.status === 404) return null;
		return null;
	} catch (error) {
		console.error('Failed to fetch RawMaterialControl', error);
		return null;
	}
}

export async function getAllRawMaterialControlsGrouped(): Promise<RawMaterialControlGrouped | null> {
	try {
		const response = await fetch(`${BASE_URL}/all-grouped`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (response.ok) {
			const data: RawMaterialControlGrouped = await response.json();
			console.log(data);
			return data;
		}

		return null;
	} catch (error) {
		console.error('Failed to fetch all RawMaterialControl summaries', error);
		return null;
	}
}

export async function getRawMaterialControlsFiltered(query: string): Promise<RawMaterialControlGrouped | null> {
try {
		const response = await fetch(`${BASE_URL}/all-filtered?search=${encodeURIComponent(query)}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (response.ok) {
			const data: RawMaterialControlGrouped = await response.json();
			console.log(data);
			return data;
		}

		return null;
	} catch (error) {
		console.error('Failed to fetch filtered RawMaterialControl summaries', error);
		return null;
	}
}



export async function getAllRawMaterialControlSummaries(): Promise<RawMaterialControlDTO[] | null> {
	try {
		const response = await fetch(`${BASE_URL}/all`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			const data = await response.json();
			console.log(data);
			return data;
		}
		return null;
	} catch (error) {
		console.error('Failed to fetch all RawMaterialControl summaries', error);
		return null;
	}
}
export async function deleteRawMaterialControl(uuid: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to delete RawMaterialControl', error);
		return false;
	}
}

export async function cancelRawMaterialControl(uuid: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}/cancel`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to cancel RawMaterialControl', error);
		return false;
	}
}

export async function DownloadRawMaterialControlPdf(uuid: string, fallbackFilename?: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/pdf/${uuid}`, {
			method: 'GET',
			headers: {
				'Accept': 'application/pdf',
			},
		});

		if (!response.ok) {
			console.error('Failed to download RawMaterialControl PDF', response.status, response.statusText);
			return false;
		}

		const blob = await response.blob();
		const filename = getFilenameFromContentDisposition(
			response.headers.get('content-disposition'),
			fallbackFilename || `report-${uuid}.pdf`,
		);

		const blobUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(blobUrl);

		return true;
	} catch (error) {
		console.error('Failed to download RawMaterialControl PDF', error);
		return false;
	}
}
